import { useState, type FormEvent } from 'react'
import { api, ApiFailure, reportScreen } from '../api.ts'
import { Avatar, BackButton, Button, EmptyState, EndpointRow, Field, Form, FormError, LinkButton, ModalFrame, PageHeader, StateView, Toast, useResource } from '../components.tsx'
import { t } from '../contracts.ts'
import { navigate } from '../router.ts'
import type { Endpoint, Page, Subscription, ViewState } from '../types.ts'

export function ChannelsScreen() {
  const resource = useResource('channels', () => api<Page<Endpoint>>('/v1/channels'), [])
  const [toast] = useState(() => {
    const value = sessionStorage.getItem('bellhook.channelToast')
    sessionStorage.removeItem('bellhook.channelToast')
    return value
  })
  return <main className="screen"><PageHeader title={t('channels.title')} action={<LinkButton to="/channels/join" tone="primary">{t('cta.joinChannel')}</LinkButton>} />{resource.state === 'ready' && <div className="list">{resource.data!.items.map((channel) => <EndpointRow endpoint={channel} member key={channel.id} />)}</div>}{resource.state === 'empty' && <EmptyState titleKey="channels.empty.title" bodyKey="channels.empty.body" action={<LinkButton to="/channels/join">{t('cta.joinChannel')}</LinkButton>} />}{!['ready', 'empty'].includes(resource.state) && <StateView state={resource.state} titleKey="channels.error.title" onRetry={resource.reload} />}<Toast message={toast} /></main>
}

function inviteCode(value: string): string {
  const trimmed = value.trim().toLowerCase()
  try {
    const url = new URL(trimmed)
    return url.pathname.split('/').filter(Boolean).at(-1) ?? ''
  } catch { return trimmed }
}

export function ChannelJoinScreen() {
  const [value, setValue] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  async function submit(event: FormEvent) {
    event.preventDefault(); setSubmitting(true); setError(null)
    const code = inviteCode(value)
    try {
      await api<Endpoint>(`/v1/join/${encodeURIComponent(code)}`)
      navigate(`/join/${encodeURIComponent(code)}`)
    } catch (failure) {
      const codeValue = failure instanceof ApiFailure ? failure.problem.code : ''
      setError(t(codeValue === 'already_member' ? 'channelJoin.error.alreadyIn' : codeValue === 'join_limit_reached' ? 'channelJoin.error.limit' : codeValue === 'invalid_invite' ? 'channelJoin.error.invalid' : 'channelJoin.error.failed'))
    } finally { setSubmitting(false) }
  }
  async function paste() {
    try { setValue(await navigator.clipboard.readText()) } catch { setError(t('channelJoin.error.failed')) }
  }
  return <main className="screen"><ModalFrame title={t('channelJoin.title')} onCancel={() => navigate('/channels')}><Form onSubmit={submit}><Field label={t('channelJoin.codeLabel')}><input value={value} placeholder={t('channelJoin.codePlaceholder')} onChange={(event) => setValue(event.target.value)} required /></Field><Button type="button" onClick={paste}>{t('cta.paste')}</Button><FormError>{error}</FormError><Button tone="primary" type="submit" disabled={submitting}>{submitting ? t('common.loading') : t('cta.join')}</Button></Form></ModalFrame></main>
}

export function ChannelPreviewScreen({ code, signedIn }: { code: string; signedIn: boolean }) {
  const [joining, setJoining] = useState(false)
  const [stateOverride, setStateOverride] = useState<ViewState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const resource = useResource('channelPreview', async () => {
    if (!signedIn) {
      if (!window.__BELLHOOK_PREVIEW__) throw new ApiFailure(404, { code: 'invalid_invite', message: '', requestId: '', field: null, retryAfterSeconds: null })
      return { id: code, mode: 'channel', viewerRole: 'member', joinPolicy: 'code', historyVisibility: 'full', subscriptionId: null, muted: false, createdAt: '', lastDeliveryAt: null, ...window.__BELLHOOK_PREVIEW__ } as Endpoint
    }
    return api<Endpoint>(`/v1/join/${encodeURIComponent(code)}`)
  }, [code, signedIn])
  async function join() {
    if (!signedIn) {
      sessionStorage.setItem('bellhook.pendingPath', `/join/${code}`)
      navigate('/signin')
      return
    }
    setJoining(true); setError(null)
    try {
      const subscription = await api<Subscription>(`/v1/join/${encodeURIComponent(code)}`, { method: 'POST', body: '{}' })
      if (subscription.status === 'pending') setStateOverride('pending')
      else navigate(`/channels/${subscription.endpointId}`)
    } catch (failure) {
      const problem = failure instanceof ApiFailure ? failure.problem.code : ''
      setStateOverride(problem === 'banned' ? 'banned' : problem === 'channel_full' ? 'full' : problem === 'invalid_invite' || problem === 'join_closed' ? 'closed' : 'error')
      setError(t('channelJoin.error.failed'))
      void reportScreen('channelPreview', problem || 'server_error')
    } finally { setJoining(false) }
  }
  const state = stateOverride ?? resource.state
  const endpoint = resource.data
  if (state === 'pending') return <main className="screen narrow"><BackButton fallback="/channels" /><div className="state-card"><h1>{t('channelPreview.pending.title')}</h1><p>{t('channelPreview.pending.body')}</p><LinkButton to="/channels">{t('cta.backToChannels')}</LinkButton></div></main>
  if (!endpoint || state !== 'ready') {
    const key = state === 'full' ? 'channelPreview.full.title' : state === 'banned' ? 'channelPreview.banned.title' : state === 'closed' || state === 'notFound' ? 'channelPreview.closed.title' : 'channelPreview.error.title'
    return <main className="screen narrow"><BackButton fallback="/channels" /><StateView state={state} titleKey={key} onRetry={state === 'error' ? resource.reload : undefined} parentPath="/channels" /></main>
  }
  return <main className="screen narrow"><BackButton fallback="/channels" /><article className="card detail-grid"><Avatar endpoint={endpoint} size="lg" /><h1>{endpoint.name}</h1>{endpoint.description && <p>{endpoint.description}</p>}{endpoint.hostName && <p>{t('channelPreview.hostedBy', { name: endpoint.hostName })}</p>}{endpoint.memberCount !== null && <p>{t('channelPreview.memberCount', { count: endpoint.memberCount })}</p>}<FormError>{error}</FormError><Button tone="primary" disabled={joining} onClick={join}>{joining ? t('common.loading') : endpoint.joinPolicy === 'approval' ? t('cta.askToJoin') : t('cta.join')}</Button><LinkButton to="/channels">{t('cta.backToChannels')}</LinkButton></article></main>
}
